import { schemaResolver, useForm } from '@mantine/form';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { POLICY_TYPE_OPTIONS } from '../constants/fieldOptions.ts';
import { INSURER_OPTIONS } from '../constants/insurers.ts';
import type {
  ApproveInsurancePolicyInfo,
  InsurancePolicyInfo,
} from '../model.ts';
import { requiredDate, requiredText } from '../utils/formValidation.ts';
import { z } from 'zod';
import { RocDateInput } from './RocDateInput.tsx';

type InsurancePolicyEntryFormValues = {
  plateNumber: string;
  policyType: string;
  expiryDate: string | null;
  insurer: string;
};

type InsurancePolicyFormValues = {
  entries: InsurancePolicyEntryFormValues[];
};

type InsurancePolicyFormProps = {
  formId: string;
  initialInfo?: InsurancePolicyInfo;
  readOnly?: boolean;
  onValidSubmit: (info: ApproveInsurancePolicyInfo) => void;
};

const getEntryTitle = (index: number) => `第 ${index + 1} 張保單`;

const READABLE_DISABLED_STYLES = {
  input: {
    color: 'var(--mantine-color-text)',
    opacity: 1,
  },
} as const;

const createEmptyEntry = (): InsurancePolicyEntryFormValues => ({
  plateNumber: '',
  policyType: '',
  expiryDate: null,
  insurer: '',
});

const buildInitialValues = (
  info?: InsurancePolicyInfo,
): InsurancePolicyFormValues => {
  const entries = info?.entries?.map((entry) => ({
    plateNumber: entry.plateNumber ?? '',
    policyType: entry.policyType ?? '',
    expiryDate: entry.expiryDate ?? null,
    insurer: entry.insurer ?? '',
  }));

  return { entries: entries?.length ? entries : [createEmptyEntry()] };
};

const InsurancePolicyEntryFormSchema = z.object({
  plateNumber: requiredText('請輸入車牌號碼'),
  policyType: requiredText('請選擇險種'),
  expiryDate: requiredDate('請選擇車險到期日'),
  insurer: requiredText('請選擇保險公司'),
});

const InsurancePolicyFormSchema = z.object({
  entries: z.array(InsurancePolicyEntryFormSchema),
});

const InsurancePolicyForm = ({
  formId,
  initialInfo,
  readOnly = false,
  onValidSubmit,
}: InsurancePolicyFormProps) => {
  const form = useForm<InsurancePolicyFormValues>({
    initialValues: buildInitialValues(initialInfo),
    validate: schemaResolver(InsurancePolicyFormSchema, { sync: true }),
  });

  const handleSubmit = (values: InsurancePolicyFormValues) => {
    onValidSubmit({
      entries: values.entries.map((entry) => ({
        plateNumber: entry.plateNumber.trim(),
        policyType: entry.policyType as 'COMPULSORY' | 'VOLUNTARY',
        expiryDate: entry.expiryDate!,
        insurer: entry.insurer,
      })),
    });
  };

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {form.getValues().entries.map((_, index) => (
          <Card key={form.key(`entries.${index}`)} withBorder radius="md">
            <Group justify="space-between" mb="sm">
              <Text fw={700}>{getEntryTitle(index)}</Text>
              {!readOnly && index > 0 && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={`刪除${getEntryTitle(index)}`}
                  onClick={() => form.removeListItem('entries', index)}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              )}
            </Group>

            <Stack gap="sm">
              <TextInput
                label="車牌號碼"
                placeholder="請輸入車牌號碼"
                withAsterisk
                disabled={readOnly}
                styles={READABLE_DISABLED_STYLES}
                {...form.getInputProps(`entries.${index}.plateNumber`)}
              />
              <Radio.Group
                label="險種"
                withAsterisk
                {...form.getInputProps(`entries.${index}.policyType`)}
              >
                <Group mt="xs">
                  {POLICY_TYPE_OPTIONS.map(({ value, label }) => (
                    <Radio
                      key={value}
                      value={value}
                      label={label}
                      disabled={readOnly}
                    />
                  ))}
                </Group>
              </Radio.Group>
              <RocDateInput
                label="車險到期日"
                placeholder="請選擇車險到期日"
                withAsterisk
                disabled={readOnly}
                styles={READABLE_DISABLED_STYLES}
                {...form.getInputProps(`entries.${index}.expiryDate`)}
              />
              <Select
                label="保險公司"
                placeholder="請選擇保險公司"
                data={INSURER_OPTIONS}
                searchable
                withAsterisk
                disabled={readOnly}
                styles={READABLE_DISABLED_STYLES}
                {...form.getInputProps(`entries.${index}.insurer`)}
              />
            </Stack>
          </Card>
        ))}

        {!readOnly && (
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() => form.insertListItem('entries', createEmptyEntry())}
          >
            新增保單
          </Button>
        )}
      </Stack>
    </form>
  );
};

export { InsurancePolicyForm };
